import { memo } from 'react';
import clsx from 'clsx';
import DisplayField from '@molecules/DisplayField/DisplayField';
import { decodeHtmlEntities } from '@srm/ui/lib/html';
import { IDataGridColumnDescription } from '@models/response/IGridDescriptionResponse';
import { createAddressBookCell } from '@utils/addressBookCell';
import { ValueType } from '@srm/ui/types/ValueType';
import { IDataGridCellDeps, withLink } from '../cellLink';
import { DataGridRow, rowValue } from '../dataGridSchema';

/** Типы, которые DisplayField отдаёт простым текстом. */
const PLAIN_TYPES = new Set<ValueType>([
    ValueType.Text,
    ValueType.LongText,
    ValueType.Integer,
    ValueType.Double,
    ValueType.Year,
    ValueType.AddressBook,
]);

export interface IValueCellProps {
    row: DataGridRow;
    field: string;
    type: ValueType;
    description?: IDataGridColumnDescription;
    deps: IDataGridCellDeps;
}

/**
 * Ячейка значения серверного списка. Мемоизирована: прокрутка перерисовывает
 * список на каждый кадр, а содержимое ячейки (DisplayField, hover-карточка,
 * ссылка) — самое дорогое, что в ней есть, и от скролла оно не зависит.
 */
const ValueCell = memo(({ row, field, type, description, deps }: IValueCellProps) => {
    const value = rowValue(row, field);
    // ФИО-колонка с ключом адресной книги — имя становится триггером hover-карточки
    const abCell = deps.readOnly
        ? null
        : createAddressBookCell({ rowType: 'data', data: row, value }, description, deps.wrapClassName);
    if (abCell) return withLink(row, deps, abCell);

    // Текстовые типы DisplayField рисует тем же div.content-text — в списке из
    // сотен ячеек его свитч и обвязка дают заметную долю кадра, поэтому простые
    // значения рендерим напрямую, а форматирование (даты, деньги, ссылки,
    // чекбоксы) оставляем DisplayField.
    const rendered = PLAIN_TYPES.has(type) ? (
        <div className={clsx('content-text', deps.wrapClassName)}>{decodeHtmlEntities(String(value ?? ''))}</div>
    ) : (
        <DisplayField className={deps.wrapClassName} type={type} value={value} />
    );
    return type === ValueType.Url ? rendered : withLink(row, deps, rendered);
});
ValueCell.displayName = 'ValueCell';

export default ValueCell;
